

import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  Paper,
  Fab,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Settings,
  AccountTree,
  Delete,
  Edit,
  Save,
  Visibility,
  Assignment,
  TrendingUp,
  Business,
  Person,
  RequestQuote,
  ShoppingCart,
  Receipt,
  RecordVoiceOver,
  Task,
  Email,
  Update,
  Create,
  Link,
  CheckCircle,
  Cancel,
  Warning,
  ZoomIn,
  ZoomOut,
  ZoomOutMap
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useTheme } from '@mui/material/styles';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import AutomationWizard from './AutomationWizard';
import { v2 as v2t } from 'styles/tokens/v2';
import { mockObjects, mockActionTypes } from 'data/mockAutomationData';


const objectIcons = {
  Lead: Assignment,
  Deal: TrendingUp,
  Account: Business,
  Contact: Person,
  Quote: RequestQuote,
  Order: ShoppingCart,
  Invoice: Receipt,
  Echo: RecordVoiceOver
};

const actionIcons = {
  'Create Task': Task,
  'Send Email': Email,
  'Update Field': Update,
  'Create Record': Create,
  'Update Related Record': Link
};

const nodeTypes = {
  trigger: {
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    icon: PlayArrow,
    label: 'Trigger'
  },
  condition: {
    color: '#fb8c00',
    gradient: 'linear-gradient(135deg, #fb8c00 0%, #FFA726 100%)',
    icon: Settings,
    label: 'Condition'
  },
  action: {
    color: '#1565C8',
    gradient: 'linear-gradient(135deg, #1565C8 0%, #5B9BD5 100%)',
    icon: AccountTree,
    label: 'Action'
  },
  end: {
    color: '#F44335',
    gradient: 'linear-gradient(135deg, #F44335 0%, #EF5350 100%)',
    icon: CheckCircle,
    label: 'End'
  }
};

// Classic stat tile fills, keyed by the tile's border colour.
const CLASSIC_TILE_GRADIENT = {
  '76, 175, 80': 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(102, 187, 106, 0.05) 100%)',
  '251, 140, 0': 'linear-gradient(135deg, rgba(251, 140, 0, 0.1) 0%, rgba(255, 167, 38, 0.05) 100%)',
  '0, 158, 219': 'linear-gradient(135deg, rgba(0, 158, 219, 0.1) 0%, rgba(51, 183, 230, 0.05) 100%)',
  '244, 67, 53': 'linear-gradient(135deg, rgba(244, 67, 53, 0.1) 0%, rgba(239, 83, 80, 0.05) 100%)',
};

function FlowBuilder({ automation, onBack, onSave }) {
  const isV2 = useTheme()?.pearlUi === 'v2';
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [addNodeMenu, setAddNodeMenu] = useState(null);
  const [nodeDetailsDialog, setNodeDetailsDialog] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [wasDragged, setWasDragged] = useState(false);

  React.useEffect(() => {
    if (automation) {
      // Convert automation data to flow nodes
      const flowNodes = [];
      let nodeId = 1;

      // Add trigger node - centered horizontally
      flowNodes.push({
        id: `node-${nodeId++}`,
        type: 'trigger',
        position: { x: 200, y: 50 },
        sourcePosition: 'bottom',
        targetPosition: 'top',
        data: {
          object: automation.trigger_object || automation.object,
          trigger: automation.trigger_event || automation.trigger,
          title: `${automation.trigger_object || automation.object} ${automation.trigger_event || automation.trigger}`,
          subtitle: 'Trigger Event'
        }
      });

      // Add condition nodes based on trigger_conditions with better spacing
      if (automation.trigger_conditions && automation.trigger_conditions.length > 0) {
        automation.trigger_conditions.forEach((conditionGroup, groupIndex) => {
          // Calculate position to prevent overlap
          const xPos = 50 + (groupIndex * 320); // Increased spacing to 320px
          flowNodes.push({
            id: `node-${nodeId++}`,
            type: 'condition',
            position: { x: xPos, y: 220 },
            sourcePosition: 'bottom',
            targetPosition: 'top',
            data: {
              groupName: conditionGroup.name || `Group ${groupIndex + 1}`,
              logic: conditionGroup.logic || 'AND',
              conditions: conditionGroup.conditions || [],
              title: conditionGroup.name || `Condition Group ${groupIndex + 1}`,
              subtitle: `${conditionGroup.logic || 'AND'} logic`
            }
          });
        });
      }

      // Add action nodes with proper spacing to prevent overlap
      if (automation.actions && automation.actions.length > 0) {
        automation.actions.forEach((action, index) => {
          // Calculate position to prevent overlap
          const xPos = 50 + (index * 320); // Increased spacing to 320px
          flowNodes.push({
            id: `node-${nodeId++}`,
            type: 'action',
            position: { x: xPos, y: 390 },
            sourcePosition: 'bottom',
            targetPosition: 'top',
            data: {
              actionType: action.type,
              parameters: action.parameters,
              timeDelay: action.timeDelay,
              title: action.type,
              subtitle: getActionSubtitle(action)
            }
          });
        });
      }

      // Add end node - centered below actions
      flowNodes.push({
        id: `node-${nodeId++}`,
        type: 'end',
        position: { x: 200, y: 560 },
        targetPosition: 'top',

        data: {
          title: 'End',
          subtitle: 'Flow Complete'
        }
      });

      setNodes(flowNodes);
    }
  }, [automation]);

  // Helper function to get action subtitle
  const getActionSubtitle = (action) => {
    switch (action.type) {
      case 'Create Record':
        return `Create ${action.parameters?.recordType || 'Record'}`;
      case 'Update Field':
        return `Update ${action.parameters?.field_name || 'Field'}`;
      case 'Send Email':
        return action.parameters?.subject || 'Send Email';
      case 'Create Task':
        return action.parameters?.subject || 'Create Task';
      case 'Webhook':
        return action.parameters?.name || 'Webhook';
      case 'Update Related Record':
        return `Update ${action.parameters?.related_object_type || 'Related Record'}`;
      default:
        return action.type;
    }
  };

  const handleAddNode = (nodeType) => {
    const existingNodesOfType = nodes.filter(n => n.type === nodeType);
    const baseY = nodeType === 'trigger' ? 50 :
      nodeType === 'condition' ? 220 :
        nodeType === 'action' ? 390 : 560;

    const newNode = {
      id: `node-${Date.now()}`,
      type: nodeType,
      // position: { 
      //   x: 50 + (existingNodesOfType.length * 320), // Increased spacing to prevent overlap
      //   y: baseY 
      // },
      position: {
        x: 200 + (existingNodesOfType.length * 320), // center + spacing
        y: baseY
      },
      sourcePosition: 'bottom',
      targetPosition: 'top',
      data: {
        title: nodeTypes[nodeType].label,
        subtitle: 'New Node'
      }
    };
    setNodes(prev => [...prev, newNode]);
    setAddNodeMenu(null);
  };

  // Custom drag handlers for better positioning
  const handleMouseDown = useCallback((e, node) => {
    if (e.button !== 0) return; // Only handle left mouse button
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const canvasContainer = e.currentTarget.closest('div[class*="flow-canvas"]').parentElement;
    const canvasRect = canvasContainer.getBoundingClientRect();

    setIsDragging(true);
    setDraggedNode(node);
    setWasDragged(false); // Reset drag flag

    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;

    setDragOffset({ x: offsetX, y: offsetY });

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();

      // Check if mouse moved enough to be considered a drag
      const deltaX = Math.abs(moveEvent.clientX - startX);
      const deltaY = Math.abs(moveEvent.clientY - startY);

      if (deltaX > 5 || deltaY > 5) {
        setWasDragged(true);
      }

      // Calculate position relative to the scrollable container
      const scrollLeft = canvasContainer.scrollLeft;
      const scrollTop = canvasContainer.scrollTop;

      const newX = (moveEvent.clientX - canvasRect.left + scrollLeft - offsetX) / zoomLevel;
      const newY = (moveEvent.clientY - canvasRect.top + scrollTop - offsetY) / zoomLevel;

      setNodes(prev => prev.map(n => {
        if (n.id === node.id) {
          return {
            ...n,
            position: {
              x: Math.max(20, Math.min(newX, 2000)), // Increased max bounds
              y: Math.max(20, Math.min(newY, 2000))
            }
          };
        }
        return n;
      }));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDraggedNode(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [zoomLevel]);

  const handleDragStart = useCallback((start) => {
    const draggedNode = nodes.find(node => node.id === start.draggableId);
    setDraggedNode(draggedNode);
  }, [nodes]);

  const handleDragEnd = useCallback((result) => {
    setDraggedNode(null);
  }, []);

  const handleNodeClick = (node) => {
    // Only open details if it wasn't a drag operation
    if (!wasDragged) {
      setSelectedNode(node);
      setNodeDetailsDialog(true);
    }
    // Reset the drag flag for next interaction
    setTimeout(() => setWasDragged(false), 100);
  };

  const handleNodeDelete = (nodeId) => {
    setNodes(prev => prev.filter(node => node.id !== nodeId));
    setConnections(prev => prev.filter(conn =>
      conn.source !== nodeId && conn.target !== nodeId
    ));
  };

  const handleEditFlow = () => {
    setShowWizard(true);
  };

  const handleSaveFlow = () => {
    // Convert nodes back to automation format
    const triggerNode = nodes.find(n => n.type === 'trigger');
    const conditionNodes = nodes.filter(n => n.type === 'condition');
    const actionNodes = nodes.filter(n => n.type === 'action');

    const automationData = {
      name: automation?.name || 'Untitled Flow',
      trigger_object: triggerNode?.data?.object || '',
      trigger_event: triggerNode?.data?.trigger || '',
      trigger_conditions: conditionNodes.map(node => ({
        id: node.id,
        name: node.data.groupName,
        logic: node.data.logic,
        logicMode: 'simple',
        conditions: node.data.conditions,
        customLogic: ''
      })),
      actions: actionNodes.map(node => ({
        type: node.data.actionType || '',
        parameters: node.data.parameters || {},
        timeDelay: node.data.timeDelay || { enabled: false, days: 0, hours: 0, minutes: 0 }
      })),
      is_active: automation?.is_active !== undefined ? automation.is_active : true
    };

    onSave(automationData);
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setCanvasOffset({ x: 0, y: 0 });
  };

  // Mouse wheel zoom functionality
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel(prev => Math.max(0.5, Math.min(2, prev + delta)));
    }
  }, []);



  const renderNode = (node, index) => {
    const nodeType = nodeTypes[node.type];
    const IconComponent = nodeType.icon;
    const isBeingDragged = draggedNode?.id === node.id;

    return (
      <Card
        key={node.id}
        sx={{
          position: 'absolute',
          left: node.position.x,
          top: node.position.y,
          width: 200,
          minHeight: 120,
          cursor: isBeingDragged ? 'grabbing' : 'grab',
          transform: isBeingDragged ? 'rotate(2deg) scale(1.03)' : hoveredNode === node.id ? 'translateY(-2px) scale(1.01)' : 'none',
          boxShadow: isBeingDragged
            ? `0 15px 50px rgba(0,0,0,0.3), 0 0 0 2px ${nodeTypes[node.type].color}`
            : selectedNode?.id === node.id
              ? `0 6px 20px rgba(0,0,0,0.15), 0 0 0 2px ${nodeTypes[node.type].color}`
              : hoveredNode === node.id
                ? '0 8px 25px rgba(0,0,0,0.15)'
                : '0 3px 12px rgba(0,0,0,0.08)',
          border: selectedNode?.id === node.id ? `2px solid ${nodeTypes[node.type].color}` : '1px solid rgba(0,0,0,0.08)',
          borderRadius: '12px',
          background: isBeingDragged ? 'rgba(255,255,255,0.98)' : 'white',
          transition: isBeingDragged ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: isBeingDragged ? 1000 : 50, // Lower than connection lines (100)
          userSelect: 'none',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px) scale(1.01)'
          },
          // Connection handles - hidden as requested
          '&::before': {
            display: 'none'
          },
          '&::after': {
            display: 'none'
          }
        }}
        onClick={() => handleNodeClick(node)}
        onMouseEnter={() => setHoveredNode(node.id)}
        onMouseLeave={() => setHoveredNode(null)}
        onMouseDown={(e) => handleMouseDown(e, node)}
      >
        <MDBox p={2.5}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: nodeType.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: `0 4px 12px ${nodeType.color}40`,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: '-2px',
                  borderRadius: '50%',
                  background: nodeType.gradient,
                  zIndex: -1,
                  opacity: hoveredNode === node.id ? 0.3 : 0,
                  transition: 'opacity 0.2s ease'
                }
              }}
            >
              <IconComponent sx={{ fontSize: 20, color: 'white !important' }} />
            </Box>
            <Box flex={1}>
              <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                {nodeType.label}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleNodeDelete(node.id);
              }}
            >
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 0.5, color: '#344767' }}>
            {node.data.title}
          </Typography>

          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.7)' }}>
            {node.type === 'condition' && node.data.conditions ?
              `${node.data.conditions.length} condition(s)` :
              node.data.subtitle}
          </Typography>

          {node.type === 'condition' && node.data.conditions && node.data.conditions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {node.data.conditions.slice(0, 2).map((condition, idx) => (
                <Chip
                  key={idx}
                  label={`${condition.field} ${condition.operator}`}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: '18px',
                    mr: 0.5,
                    mb: 0.5,
                    background: 'linear-gradient(135deg, #fb8c00 0%, #FFA726 100%)',
                    color: 'white !important',
                    fontWeight: 500,
                    borderRadius: '9px',
                    '& .MuiChip-label': {
                      color: 'white !important'
                    }
                  }}
                />
              ))}
              {node.data.conditions.length > 2 && (
                <Chip
                  label={`+${node.data.conditions.length - 2} more`}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: '18px',
                    background: 'linear-gradient(135deg, #fb8c00 0%, #FFA726 100%)',
                    color: 'white !important',
                    fontWeight: 500,
                    borderRadius: '9px',
                    '& .MuiChip-label': {
                      color: 'white !important'
                    }
                  }}
                />
              )}
            </Box>
          )}

          {node.type === 'action' && node.data.timeDelay && node.data.timeDelay.enabled && (
            <Box sx={{ mt: 1 }}>
              <Chip
                label={`Delay: ${node.data.timeDelay.days}d ${node.data.timeDelay.hours}h ${node.data.timeDelay.minutes}m`}
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  height: '18px',
                  background: 'linear-gradient(135deg, #1565C8 0%, #5B9BD5 100%)',
                  color: 'white !important',
                  fontWeight: 500,
                  borderRadius: '9px',
                  '& .MuiChip-label': {
                    color: 'white !important'
                  }
                }}
              />
            </Box>
          )}
        </MDBox>
      </Card>
    );
  };

  const renderToolbar = () => (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 20,
        left: '60%',
        transform: 'translateX(-50%)',
        width: 360,           // set a fixed width for the main box
        p: 1.5,               // smaller padding
        zIndex: 1000,
        borderRadius: 4,
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        background: 'linear-gradient(135deg, info.main 0%, rgba(255,255,255,0.9) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.2)',
        display: 'flex',
        justifyContent: 'center', // center inner content horizontally
      }}
    >
      <Box display="flex"
        gap={1.5}              // smaller gap between buttons
        alignItems="center"
        justifyContent="center" // center inner items
        flexWrap="wrap"     >
        {/* <Tooltip title="Add Node">
          <Fab
            color="primary"
            size="small"
            onClick={(e) => setAddNodeMenu(e.currentTarget)}
          >
            <Add />
          </Fab>
        </Tooltip>
        
        <Divider orientation="vertical" sx={{ height: 40, mx: 1 }} /> */}

        {/* Zoom Controls */}
        <Tooltip title="Zoom In (+)">
          <Fab
            onClick={handleZoomIn}
            disabled={zoomLevel >= 2}
            sx={{
              width: 40,
              height: 40,
              minHeight: "unset",
              backgroundColor: "#000",
              color: "#fff",
              padding: 0, // zero padding so icon fills
              "& .MuiSvgIcon-root": {
                fontSize: "22px", // force bigger icon
                width: "22px",
                height: "22px"
              },
              "&:hover": {
                backgroundColor: "#000"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0 !important",
                color: "#9e9e9e !important",
              }
            }}
          >

            <ZoomIn />
          </Fab>
        </Tooltip>

        <Tooltip title="Reset Zoom (1:1)">
          <Fab
            size="medium"
            onClick={handleResetZoom}
            sx={{
              width: 40,
              height: 40,
              minHeight: "unset",
              backgroundColor: "#000",
              color: "#fff",
              padding: 0, // zero padding so icon fills
              "& .MuiSvgIcon-root": {
                fontSize: "22px", // force bigger icon
                width: "22px",
                height: "22px"
              },
              "&:hover": {
                backgroundColor: "#000"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0 !important",
                color: "#9e9e9e !important",
              }
            }}
          >
            <ZoomOutMap />
          </Fab>
        </Tooltip>

        <Tooltip title="Zoom Out (-)">
          <Fab
            size="medium"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.5}
            sx={{
              width: 40,
              height: 40,
              minHeight: "unset",
              backgroundColor: "#000",
              color: "#fff",
              padding: 0, // zero padding so icon fills
              "& .MuiSvgIcon-root": {
                fontSize: "22px", // force bigger icon
                width: "22px",
                height: "22px"
              },
              "&:hover": {
                backgroundColor: "#000"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0 !important",
                color: "#9e9e9e !important",
              }
            }}
          >
            <ZoomOut />
          </Fab>
        </Tooltip>

        {/* Zoom Level Indicator */}
        <Box
          sx={{
            minWidth: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: 2,
            fontWeight: 'bold',
            fontSize: '1rem',
            mx: 1, // equal margin both sides
            border: '1px solid #ccc'
          }}
        >
          {Math.round(zoomLevel * 100)}%
        </Box>


        {/* <Divider orientation="vertical" sx={{ height: 40, mx: 1 }} /> */}

        <Tooltip title="Edit Flow">
          <Fab
            size="small"
            onClick={handleEditFlow}
            sx={{
              width: 40,
              height: 40,
              minHeight: "unset",
              backgroundColor: "#000",
              color: "#fff",
              padding: 0, // zero padding so icon fills
              "& .MuiSvgIcon-root": {
                fontSize: "22px", // force bigger icon
                width: "22px",
                height: "22px"
              },
              "&:hover": {
                backgroundColor: "#000"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0 !important",
                color: "#9e9e9e !important",
              }
            }}
          >
            <Edit />
          </Fab>
        </Tooltip>

        <Tooltip title="Save Flow">
          <Fab
            size="small"
            onClick={handleSaveFlow}
            sx={{
              width: 40,
              height: 40,
              minHeight: "unset",
              backgroundColor: "#000",
              color: "#fff",
              padding: 0, // zero padding so icon fills
              "& .MuiSvgIcon-root": {
                fontSize: "22px", // force bigger icon
                width: "22px",
                height: "22px"
              },
              "&:hover": {
                backgroundColor: "#000"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0 !important",
                color: "#9e9e9e !important",
              }
            }}
          >
            <Save />
          </Fab>
        </Tooltip>

        {/* <Tooltip title="Preview">
          <Fab
            size="small"
            onClick={() => setIsEditMode(!isEditMode)}
          >
            <Visibility />
          </Fab>
        </Tooltip> */}
      </Box>
    </Paper>
  );

  const renderAddNodeMenu = () => (
    <Menu
      anchorEl={addNodeMenu}
      open={Boolean(addNodeMenu)}
      onClose={() => setAddNodeMenu(null)}
    >
      <MenuItem onClick={() => handleAddNode('trigger')}>
        <ListItemIcon>
          <PlayArrow />
        </ListItemIcon>
        <ListItemText primary="Trigger" />
      </MenuItem>
      <MenuItem onClick={() => handleAddNode('condition')}>
        <ListItemIcon>
          <Settings />
        </ListItemIcon>
        <ListItemText primary="Condition" />
      </MenuItem>
      <MenuItem onClick={() => handleAddNode('action')}>
        <ListItemIcon>
          <AccountTree />
        </ListItemIcon>
        <ListItemText primary="Action" />
      </MenuItem>
    </Menu>
  );

  const renderNodeDetails = () => (
    <Dialog
      open={nodeDetailsDialog}
      onClose={() => setNodeDetailsDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
          Node Details
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        {selectedNode && (
          <Box sx={{
            backgroundColor: '#f9fafb',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2
          }}>
            <Box display="flex" alignItems="center" gap={2} mb={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: nodeTypes[selectedNode.type].color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: theme => `0 6px 16px ${nodeTypes[selectedNode.type].color}55`
                }}
              >
                {(() => {
                  const IconComp = nodeTypes[selectedNode.type].icon;
                  return <IconComp sx={{ color: 'white !important', fontSize: 20 }} />;
                })()}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{selectedNode.data.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {nodeTypes[selectedNode.type].label}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 1.5 }} />

            <List dense sx={{ '& .MuiListItem-root': { py: 0.5 } }}>
              {selectedNode.type === 'trigger' && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Object"
                      secondary={selectedNode.data.object}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Trigger Event"
                      secondary={selectedNode.data.trigger}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                    />
                  </ListItem>
                </>
              )}

              {selectedNode.type === 'condition' && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Group Name"
                      secondary={selectedNode.data.groupName}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Logic"
                      secondary={selectedNode.data.logic}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Conditions"
                      secondary={`${selectedNode.data.conditions.length} condition(s)`}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                    />
                  </ListItem>
                  {selectedNode.data.conditions.map((condition, index) => (
                    <ListItem key={index} sx={{ pl: 4 }}>
                      <ListItemText
                        primary={`Condition ${index + 1}`}
                        secondary={`${condition.field} ${condition.operator} ${condition.value}`}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary' } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.82rem', color: 'text.primary' } }}
                      />
                    </ListItem>
                  ))}
                </>
              )}

              {selectedNode.type === 'action' && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Action Type"
                      secondary={selectedNode.data.actionType}
                      primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                    />
                  </ListItem>
                  {selectedNode.data.timeDelay && selectedNode.data.timeDelay.enabled && (
                    <ListItem>
                      <ListItemText
                        primary="Time Delay"
                        secondary={`${selectedNode.data.timeDelay.days}d ${selectedNode.data.timeDelay.hours}h ${selectedNode.data.timeDelay.minutes}m`}
                        primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                      />
                    </ListItem>
                  )}
                  {selectedNode.data.parameters && Object.entries(selectedNode.data.parameters).map(([key, value]) => {
                    // Skip empty or null values
                    if (value === null || value === undefined || value === '') return null;

                    // Format the key for display
                    const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                    return (
                      <ListItem key={key}>
                        <ListItemText
                          primary={displayKey}
                          secondary={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' } }}
                          secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                        />
                      </ListItem>
                    );
                  })}
                </>
              )}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={() => setNodeDetailsDialog(false)} variant="gradient"
          color="secondary">
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  // v2: plain bordered tiles; the counts are dark text (the tinted colours
  // were below AA on white) and the icon keeps the node colour.
  const statTileSx = (rgb) => (isV2 ? {
    p: 2,
    borderRadius: '8px',
    bgcolor: v2t.surface,
    border: `1px solid ${v2t.border}`,
  } : {
    p: 2,
    borderRadius: '12px',
    background: CLASSIC_TILE_GRADIENT[rgb],
    border: `1px solid rgba(${rgb}, 0.1)`,
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px rgba(${rgb}, 0.15)`
    }
  });

  const renderFlowStats = () => (
    <Card sx={isV2 ? { mb: 3 } : {
      mb: 3,
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.05)'
    }}>
      <MDBox p={3}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Box
              textAlign="center"
              sx={statTileSx('76, 175, 80')}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <PlayArrow sx={{ color: '#4CAF50', fontSize: 24, mr: 1 }} />
                <Typography variant="h4" sx={{ color: isV2 ? v2t.text : '#4CAF50', fontWeight: 700 }}>
                  {nodes.filter(n => n.type === 'trigger').length}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: isV2 ? v2t.text2 : '#344767', fontWeight: 600 }}>
                Triggers
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box
              textAlign="center"
              sx={statTileSx('251, 140, 0')}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <Settings sx={{ color: '#fb8c00', fontSize: 24, mr: 1 }} />
                <Typography variant="h4" sx={{ color: isV2 ? v2t.text : '#fb8c00', fontWeight: 700 }}>
                  {nodes.filter(n => n.type === 'condition').length}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: isV2 ? v2t.text2 : '#344767', fontWeight: 600 }}>
                Conditions
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box
              textAlign="center"
              sx={statTileSx('0, 158, 219')}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <AccountTree sx={{ color: '#1565C8', fontSize: 24, mr: 1 }} />
                <Typography variant="h4" sx={{ color: isV2 ? v2t.text : '#1565C8', fontWeight: 700 }}>
                  {nodes.filter(n => n.type === 'action').length}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: isV2 ? v2t.text2 : '#344767', fontWeight: 600 }}>
                Actions
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box
              textAlign="center"
              sx={statTileSx('244, 67, 53')}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <Link sx={{ color: '#F44335', fontSize: 24, mr: 1 }} />
                <Typography variant="h4" sx={{ color: isV2 ? v2t.text : '#F44335', fontWeight: 700 }}>
                  {connections.length}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: isV2 ? v2t.text2 : '#344767', fontWeight: 600 }}>
                Connections
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );

  // Helper function to create bezier curve path (vertically connections only)
  const createBezierPath = (x1, y1, x2, y2) => {
    const dy = y2 - y1;
    const controlPointOffset = Math.max(50, Math.abs(dy) * 0.3);

    const cp1x = x1;
    const cp1y = y1 + controlPointOffset;
    const cp2x = x2;
    const cp2y = y2 - controlPointOffset;

    return `M ${x1},${y1} C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x2},${y2}`;
  };

  const renderFlowConnections = () => {
    const connections = [];

    // Find trigger node
    const triggerNode = nodes.find(n => n.type === 'trigger');
    if (!triggerNode) return null;

    // Connect trigger to conditions
    const conditionNodes = nodes.filter(n => n.type === 'condition');
    if (conditionNodes.length > 0) {
      conditionNodes.forEach((conditionNode, index) => {
        connections.push({
          id: `conn-${triggerNode.id}-${conditionNode.id}`,
          source: triggerNode,
          target: conditionNode,
          type: 'condition'
        });
      });
    } else {
      // If no conditions, connect trigger directly to actions
      const actionNodes = nodes.filter(n => n.type === 'action');
      actionNodes.forEach((actionNode, index) => {
        connections.push({
          id: `conn-${triggerNode.id}-${actionNode.id}`,
          source: triggerNode,
          target: actionNode,
          type: 'action'
        });
      });
    }

    // Connect conditions to actions
    if (conditionNodes.length > 0) {
      const actionNodes = nodes.filter(n => n.type === 'action');
      conditionNodes.forEach((conditionNode, conditionIndex) => {
        actionNodes.forEach((actionNode, actionIndex) => {
          connections.push({
            id: `conn-${conditionNode.id}-${actionNode.id}`,
            source: conditionNode,
            target: actionNode,
            type: 'action'
          });
        });
      });
    }

    // Connect actions to end
    const actionNodes = nodes.filter(n => n.type === 'action');
    const endNode = nodes.find(n => n.type === 'end');
    if (endNode) {
      actionNodes.forEach((actionNode, index) => {
        connections.push({
          id: `conn-${actionNode.id}-${endNode.id}`,
          source: actionNode,
          target: endNode,
          type: 'end'
        });
      });
    }

    // Define connection colors based on project theme
    const connectionStyles = {
      trigger: { color: '#4CAF50', gradient: 'url(#triggerGradient)' },
      condition: { color: '#fb8c00', gradient: 'url(#conditionGradient)' },
      action: { color: '#1565C8', gradient: 'url(#actionGradient)' },
      end: { color: '#F44335', gradient: 'url(#endGradient)' }
    };

    return (
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 100 // Higher z-index to appear above cards
        }}
      >
        <defs>
          {/* Gradients for connections */}
          <linearGradient id="triggerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4CAF50" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#66BB6A" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="conditionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#fb8c00" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#FFA726" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="actionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#1565C8" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#5B9BD5" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="endGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F44335" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#EF5350" stopOpacity="0.6" />
          </linearGradient>

          {/* Enhanced arrow markers - made bigger and more visible */}
          {Object.entries(connectionStyles).map(([type, style]) => (
            <marker
              key={`arrowhead-${type}`}
              id={`arrowhead-${type}`}
              markerWidth="10"
              markerHeight="8"
              refX="9"
              refY="4"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <path
                d="M 0,0 L 0,8 L 10,4 z"
                fill={style.color}
                stroke={style.color}
                strokeWidth="0.5"
                strokeLinejoin="round"
              />
            </marker>
          ))}

          {/* Animated flow patterns */}
          <pattern id="flowPattern" patternUnits="userSpaceOnUse" width="20" height="20">
            <circle cx="2" cy="10" r="1" fill="rgba(255,255,255,0.6)">
              <animate attributeName="cx" values="2;18;2" dur="2s" repeatCount="indefinite" />
            </circle>
          </pattern>
        </defs>

        {connections.map(conn => {
          // Calculate source and target positions
          // React Flow node object
          const nodeWidth = conn.source.width || 200;
          const nodeHeight = conn.source.height || 120;
          const paddingBottom = -25; // exact bottom touch ke liye

          // source (bottom edge)
          const sourceX = conn.source.position.x + nodeWidth / 2;
          const sourceY = conn.source.position.y + nodeHeight - paddingBottom;

          // target (top edge)
          const targetX = conn.target.position.x + nodeWidth / 2;
          const targetY = conn.target.position.y;



          const style = connectionStyles[conn.type] || connectionStyles.action;
          const path = createBezierPath(sourceX, sourceY, targetX, targetY);

          return (
            <g key={conn.id}>
              {/* Main connection path - without shadows */}
              <path
                d={path}
                stroke={style.gradient}
                strokeWidth="5"
                fill="none"
                markerEnd={`url(#arrowhead-${conn.type})`}
                strokeDasharray={conn.type === 'condition' ? '10,5' : 'none'}
              >
                {/* Animated flow effect */}
                <animate
                  attributeName="stroke-dashoffset"
                  values="0;-9;0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              {/* Connection start point */}
              <circle
                cx={sourceX}
                cy={sourceY}
                r="6"
                fill={style.color}
                stroke="white"
                strokeWidth="2"
                opacity="0.9"
              />

              {/* Connection end point */}
              <circle
                cx={targetX}
                cy={targetY}
                r="6"
                fill={style.color}
                stroke="white"
                strokeWidth="2"
                opacity="0.9"
              />

              {/* Animated dots for flow direction */}
              <circle r="3" fill={style.color} opacity="0.8">
                <animateMotion dur="2.5s" repeatCount="indefinite">
                  <mpath href={`#${conn.id}-path`} />
                </animateMotion>
              </circle>

              {/* Hidden path for animation reference */}
              <path id={`${conn.id}-path`} d={path} style={{ display: 'none' }} />
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <Box>
      {renderFlowStats()}

      <Card sx={{
        height: '80vh',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'auto', // Make scrollable
            background: `
              linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%),
              radial-gradient(circle at 25px 25px, rgba(0, 158, 219, 0.05) 2px, transparent 2px),
              radial-gradient(circle at 75px 75px, rgba(251, 140, 0, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: `${100 * zoomLevel}% ${100 * zoomLevel}%, ${50 * zoomLevel}px ${50 * zoomLevel}px, ${100 * zoomLevel}px ${100 * zoomLevel}px`,
            backgroundPosition: '0 0, 0 0, 25px 25px',
            '&::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#1565C8',
              borderRadius: '6px',
              '&:hover': {
                background: '#0E4FA3',
              },
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: draggedNode ? 'rgba(0, 158, 219, 0.05)' : 'transparent',
              transition: 'background 0.3s ease',
              pointerEvents: 'none',
              zIndex: 0
            }
          }}
        >
          <Box
            className="flow-canvas"
            onWheel={handleWheel}
            sx={{
              width: `${Math.max(100, nodes.reduce((max, node) => Math.max(max, node.position.x + 250), 500))}px`,
              height: `${Math.max(100, nodes.reduce((max, node) => Math.max(max, node.position.y + 150), 800))}px`,
              minWidth: '100%',
              minHeight: '100%',
              position: 'relative',
              transform: `scale(${zoomLevel})`,
              transformOrigin: 'top left',
              transition: 'transform 0.2s ease'
            }}
          >
            {nodes.map((node, index) => renderNode(node, index))}
            {renderFlowConnections()}
          </Box>

          {nodes.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 2
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1565C8 0%, #5B9BD5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: '0 8px 32px rgba(0, 158, 219, 0.3)',
                  animation: 'pulse 2s infinite'
                }}
              >
                <AccountTree sx={{ fontSize: 48, color: 'white' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#344767' }}>
                Welcome to Flow Builder
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                Design powerful automation workflows with visual nodes and connections.
                Start by adding your first trigger node to begin building your flow.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 450, mx: 'auto', fontStyle: 'italic' }}>
                💡 Tip: Hold Ctrl/Cmd + scroll to zoom, or use the zoom controls in the toolbar
              </Typography>
              <MDButton
                variant="gradient"
                color="info"
                size="large"
                startIcon={<Add />}
                onClick={(e) => setAddNodeMenu(e.currentTarget)}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  boxShadow: '0 8px 25px rgba(0, 158, 219, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(0, 158, 219, 0.4)'
                  }
                }}
              >
                Create Your First Node
              </MDButton>

              {/* Add subtle animation styles */}
              <style jsx>{`
                @keyframes pulse {
                  0% { transform: scale(1); }
                  50% { transform: scale(1.05); }
                  100% { transform: scale(1); }
                }
              `}</style>
            </Box>
          )}
        </Box>
      </Card>

      {renderToolbar()}
      {renderAddNodeMenu()}
      {renderNodeDetails()}

      {showWizard && (
        <AutomationWizard
          open={showWizard}
          automation={automation}
          onClose={() => setShowWizard(false)}
          onSave={(data) => {
            onSave(data);
            setShowWizard(false);
          }}
        />
      )}
    </Box>
  );
}

export default FlowBuilder; 